import { FC, memo, useCallback, useContext, useState } from 'react';

import SwapVertSharpIcon from '@mui/icons-material/SwapVertSharp';
import { Typography, Tab, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

import DateRangePicker from './components/DateRangePicker/DateRangePicker';
import { BottomFilterDiv, MyTabs } from './styled';
import { FILTER_BY } from 'constants/filter';
import { FORMAT_BY } from 'constants/format';
import { SORT_DIRECTION } from 'constants/sort';
import { AppContext } from 'context';
import { useAnnouncer } from 'context/Announcer';
import { formatDateRange } from 'services/format';
import useDateFnsLocale from 'services/useDateFnsLocale';

const BottomFilterForm: FC = memo(() => {
    const { t } = useTranslation();
    const dateFnsLocale = useDateFnsLocale();
    const { direction, setDirection, filterType, setFilterType } = useContext(AppContext);
    const [open, setOpen] = useState(false);
    const { announce } = useAnnouncer();

    const sortHandleChange = useCallback(() => {
        setDirection((prev) => {
            const newDirection =
                prev === SORT_DIRECTION.Ascending ? SORT_DIRECTION.Descending : SORT_DIRECTION.Ascending;
            // Announce immediately with the new direction text
            const newDirectionText =
                newDirection === SORT_DIRECTION.Ascending ? t('form.ascending') : t('form.descending');
            announce(`${t('form.sort_by_date')}: ${newDirectionText}`);
            return newDirection;
        });
    }, [setDirection, announce, t]);

    const filterHandleChange = useCallback(
        (e: React.SyntheticEvent, newValue: string) => {
            if (!open) {
                setFilterType({ ...filterType, filterBy: newValue });
            }
        },
        [open, filterType, setFilterType]
    );

    const handleTabClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>, value: string) => {
            if (value === FILTER_BY.DateRange) {
                setOpen(true);
            }
        },
        [setOpen]
    );

    const directionText = direction === SORT_DIRECTION.Ascending ? t('form.ascending') : t('form.descending');

    return (
        <BottomFilterDiv>
            <DateRangePicker open={open} setOpen={setOpen} />
            <MyTabs
                value={filterType.filterBy}
                onChange={filterHandleChange}
                variant='standard'
                aria-label={t('accessibility.event_filter_selector')}
                TabIndicatorProps={{ style: { display: 'none' } }}
            >
                <Tab
                    value={FILTER_BY.UpcomingEvents}
                    data-testid='upcoming'
                    label={
                        <Typography color={filterType.filterBy === FILTER_BY.UpcomingEvents ? 'primary' : 'black'}>
                            {t('form.upcoming_events')}
                        </Typography>
                    }
                />
                <Tab
                    value={FILTER_BY.AllEvents}
                    data-testid='all'
                    label={
                        <Typography color={filterType.filterBy === FILTER_BY.AllEvents ? 'primary' : 'black'}>
                            {t('form.all_events')}
                        </Typography>
                    }
                />
                <Tab
                    value={FILTER_BY.DateRange}
                    onClick={(e) => handleTabClick(e, FILTER_BY.DateRange)}
                    data-testid='dateRange'
                    label={
                        <Typography
                            color={filterType.filterBy === FILTER_BY.DateRange ? 'primary' : 'black'}
                            borderBottom={'1px dashed #828282'}
                        >
                            {t('form.date_range')}
                            {filterType.dateRange
                                ? ': ' + formatDateRange(filterType.dateRange, FORMAT_BY.FullDate, dateFnsLocale)
                                : ''}
                        </Typography>
                    }
                />
            </MyTabs>
            <Button
                onClick={sortHandleChange}
                color='primary'
                size='small'
                variant='text'
                sx={{
                    minWidth: 'auto',
                    py: '.25rem',
                    pr: '0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                }}
            >
                <span style={{ color: 'black', fontSize: '1rem' }}>{`${t(
                    'form.sort_by_date'
                )}: ${directionText}`}</span>
                <SwapVertSharpIcon fontSize='small' />
            </Button>
        </BottomFilterDiv>
    );
});

export default BottomFilterForm;
